/**
 * GroupApiService - GraphQL API for Group CRUD + Member Management
 *
 * sl-jsx3.16.2: Frontend service wrapping GraphQL group operations.
 *
 * SOLID:
 * - S: Single Responsibility - Only handles group API calls
 * - D: Depends on WorkspaceServerService for GraphQL access
 */
import {
  GraphQLService,
  type WorkspaceServerService,
} from '@affine/core/modules/cloud';
import type { Permission } from '@affine/graphql';
import {
  addGroupMateMutation,
  addGroupMemberMutation,
  createGroupMutation,
  deleteGroupMutation,
  getGroupMatesQuery,
  getGroupMembersQuery,
  getGroupQuery,
  getGroupsQuery,
  removeGroupMateMutation,
  removeGroupMemberMutation,
  updateGroupMutation,
} from '@affine/graphql';
import { Service } from '@toeverything/infra';

/**
 * Group data from API responses.
 */
export interface GroupData {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  isAutoSync: boolean;
  createdAt: string;
  memberWorkspaceRoles?: Permission[];
}

/**
 * Group member data (userId only — resolve user details client-side).
 */
export interface GroupMemberData {
  id: string;
  groupId: string;
  userId: string;
}

export class GroupApiService extends Service {
  private get graphqlService(): GraphQLService | null {
    return (
      this.workspaceServerService.server?.scope.get(GraphQLService) ?? null
    );
  }

  constructor(private readonly workspaceServerService: WorkspaceServerService) {
    super();
  }

  private requireGraphQL(): GraphQLService {
    const gql = this.graphqlService;
    if (!gql) {
      throw new Error(
        'GraphQL service not available - workspace not connected to server'
      );
    }
    return gql;
  }

  /**
   * Get all groups for a workspace.
   */
  async getGroups(workspaceId: string): Promise<GroupData[]> {
    const result = await this.requireGraphQL().gql({
      query: getGroupsQuery,
      variables: { workspaceId },
    });

    return (result.groups ?? []).map(this.toGroupData);
  }

  /**
   * Get a single group by ID.
   */
  async getGroup(
    workspaceId: string,
    groupId: string
  ): Promise<GroupData | null> {
    const result = await this.requireGraphQL().gql({
      query: getGroupQuery,
      variables: { workspaceId, groupId },
    });

    const group = result.group;
    if (!group) return null;

    return this.toGroupData(group);
  }

  /**
   * Create a new group.
   */
  async createGroup(
    workspaceId: string,
    name: string,
    description?: string
  ): Promise<GroupData> {
    const result = await this.requireGraphQL().gql({
      query: createGroupMutation,
      variables: { workspaceId, name, description },
    });

    return this.toGroupData(result.createGroup);
  }

  /**
   * Update group properties.
   */
  async updateGroup(
    workspaceId: string,
    groupId: string,
    data: { name?: string; description?: string }
  ): Promise<GroupData> {
    const result = await this.requireGraphQL().gql({
      query: updateGroupMutation,
      variables: { workspaceId, groupId, ...data },
    });

    return this.toGroupData(result.updateGroup);
  }

  /**
   * Delete a group.
   */
  async deleteGroup(workspaceId: string, groupId: string): Promise<boolean> {
    const result = await this.requireGraphQL().gql({
      query: deleteGroupMutation,
      variables: { workspaceId, groupId },
    });

    return result.deleteGroup;
  }

  /**
   * Get all members of a group.
   */
  async getGroupMembers(
    workspaceId: string,
    groupId: string
  ): Promise<GroupMemberData[]> {
    const result = await this.requireGraphQL().gql({
      query: getGroupMembersQuery,
      variables: { workspaceId, groupId },
    });

    return (result.groupMembers ?? []).map(this.toGroupMemberData);
  }

  /**
   * Add a user to a group.
   */
  async addGroupMember(
    workspaceId: string,
    groupId: string,
    userId: string
  ): Promise<GroupMemberData> {
    const result = await this.requireGraphQL().gql({
      query: addGroupMemberMutation,
      variables: { workspaceId, groupId, userId },
    });

    return this.toGroupMemberData(result.addGroupMember);
  }

  /**
   * Remove a user from a group.
   */
  async removeGroupMember(
    workspaceId: string,
    groupId: string,
    userId: string
  ): Promise<boolean> {
    const result = await this.requireGraphQL().gql({
      query: removeGroupMemberMutation,
      variables: { workspaceId, groupId, userId },
    });

    return result.removeGroupMember;
  }

  /**
   * The workspace Mates in a group.
   */
  async getGroupMates(
    workspaceId: string,
    groupId: string
  ): Promise<{ id: string; name: string }[]> {
    const result = await this.requireGraphQL().gql({
      query: getGroupMatesQuery,
      variables: { workspaceId, groupId },
    });
    return result.groupMates.map(mate => ({
      id: mate.mateId,
      name: mate.name,
    }));
  }

  /**
   * Add a workspace Mate to a group: it then holds the group's roles.
   */
  async addGroupMate(
    workspaceId: string,
    groupId: string,
    mateId: string
  ): Promise<boolean> {
    const result = await this.requireGraphQL().gql({
      query: addGroupMateMutation,
      variables: { workspaceId, groupId, mateId },
    });

    return result.addGroupMate;
  }

  /**
   * Remove a workspace Mate from a group.
   */
  async removeGroupMate(
    workspaceId: string,
    groupId: string,
    mateId: string
  ): Promise<boolean> {
    const result = await this.requireGraphQL().gql({
      query: removeGroupMateMutation,
      variables: { workspaceId, groupId, mateId },
    });

    return result.removeGroupMate;
  }

  /**
   * Map GraphQL response to GroupData.
   */
  private readonly toGroupData = (group: {
    id: string;
    workspaceId: string;
    name: string;
    description: string | null;
    isAutoSync: boolean;
    createdAt: string;
    memberWorkspaceRoles?: Permission[];
  }): GroupData => ({
    id: group.id,
    workspaceId: group.workspaceId,
    name: group.name,
    description: group.description,
    isAutoSync: group.isAutoSync,
    createdAt: group.createdAt,
    memberWorkspaceRoles: group.memberWorkspaceRoles,
  });

  /**
   * Map GraphQL response to GroupMemberData.
   */
  private readonly toGroupMemberData = (member: {
    id: string;
    groupId: string;
    userId: string;
  }): GroupMemberData => ({
    id: member.id,
    groupId: member.groupId,
    userId: member.userId,
  });
}
